import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Meta, MetaDefinition, Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { addGalleryPassword } from '../lib/add-gallery-password';
import { calcResizedUrlFromUuid } from '../lib/calc-resized-url';
import { getAllRouteData } from '../lib/get-all-route-data';
import { getFullPath } from '../lib/get-full-path';
import { isNotFoundRoute } from '../lib/is-not-found-route';
import { PICFLOW_STORYBLOK } from '../picflow-storyblok';
import { AppConfigService } from '../providers/config.service';
import { BrowserService } from './browser.service';
import { StateService } from './state.service';
import { Schema, StoryblokSchemaService } from './storyblok-schema.service';

@Injectable({
  providedIn: 'root',
})
export class MetaService {
  private meta = inject(Meta);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private state = inject(StateService);
  private title = inject(Title);
  private browserService = inject(BrowserService);
  private config = inject(AppConfigService);
  private storyblokService = inject(PICFLOW_STORYBLOK);
  private storyblokSchemaService = inject(StoryblokSchemaService);
  private document = inject<Document>(DOCUMENT);
  lastUrl: string;
  favIcons = ['.png', '.svg', '.ico', 'apple-touch-icon_GALLERY.png'];
  manifest: HTMLLinkElement;

  constructor() {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) this.handleMeta(event.urlAfterRedirects);
    });
    this.manifest = this.document.querySelector('#manifest');
  }

  public async getTitle(url: string, tags?: MetaDefinition[]) {
    if (!tags) tags = await this.getMetaTags(url, true);
    return tags.find(tag => tag.property === 'og:title').content;
  }

  private getFaviconPrefix(suffix: string) {
    const route = getFullPath(this.route.snapshot);
    let favicon = '/assets/favicon/favicon' + suffix;
    const isShared = route[1] === 'home' && this.state.sharedWithMeActiveTenant$.value;

    if (!isShared) {
      if (this.state.unseenActivity) favicon = '/assets/favicon/favicon-activity' + suffix;

      if (this.config.isTenantDomain) {
        const data = getAllRouteData(this.route.snapshot);
        const tenant = data.tenant || data.gallery?.tenant;
        const gallery = data.gallery;

        if (gallery && suffix.includes('GALLERY')) {
          if (gallery.teaser?.uuid) {
            const uuid = gallery.teaser.uuid;
            return `https://${this.config.env === 'production' ? '' : 'dev.'}picflow.media/images/resized/512x512/${uuid}.jpg`;
          }
        }

        if (tenant?.favicon_url) {
          favicon = tenant.favicon_url;
          const url = tenant.favicon_url;
          if (!url.includes('/original/')) return favicon; // new icons
          const uuid = url.split('/original/')[1].split('.')[0];
          favicon = calcResizedUrlFromUuid(this.config.assetsEndpoint, uuid, `512x512`);
        }
      } else {
        if (suffix.includes('GALLERY')) {
          return;
        }
      }
    }

    return favicon;
  }

  private getManifest() {
    if (!this.config.isTenantDomain) return;
    const data = getAllRouteData(this.route.snapshot);
    const tenant = data.tenant || data.gallery?.tenant;
    if (!tenant) return;
    if (!data.gallery) return `/manifest/${tenant.path}`;
    return addGalleryPassword(`/manifest/${tenant.id}/${data.gallery.id}`, data.gallery.password);
  }

  public setTitle(title: string) {
    const path = getFullPath(this.route.snapshot);
    if (path[0] === 'a') {
      if (path[1] === 'gallery') {
        const galleryId = path[2];
        const count = this.state.unseenActivityCountMap.get(galleryId);
        if (count) {
          const countString = count > 99 ? '99+' : count.toString();
          title = `(${countString}) ${title}`;
        }
      } else if (this.state.unseenActivity > 0) {
        const isShared = path[1] === 'home' && this.state.sharedWithMeActiveTenant$.value;

        if (!isShared) {
          const count = this.state.unseenActivity;
          const countString = count > 99 ? '99+' : count.toString();
          title = `(${countString}) ${title}`;
        }
      }
    }

    this.title.setTitle(title);
  }

  public async handleMeta(url: string = this.lastUrl) {
    if (!url && !this.lastUrl) return;
    this.lastUrl = url;
    const tags = await this.getMetaTags(url);
    const title = await this.getTitle(url, tags);

    this.favIcons.forEach(suffix => {
      const el = this.document.getElementById(`FAVICON_${suffix}`) as HTMLLinkElement;
      if (el) {
        const href = this.getFaviconPrefix(suffix);
        if (!href) return;
        el.href = href;
      }
    });

    const manifestHref = this.getManifest() || '/manifest.webmanifest';
    if (this.manifest && !this.manifest.href.endsWith(manifestHref)) {
      this.manifest.href = manifestHref;
    }

    this.setTitle(title);

    const link: HTMLLinkElement = this.document.createElement('link');
    link.setAttribute('rel', 'canonical');
    this.document.head.appendChild(link);
    link.setAttribute('href', this.getCanonical());

    const schemaScript = this.document.querySelector('script[type="application/ld+json"]');
    if (schemaScript) {
      const schema = tags.find(tag => tag.name === 'schema');
      if (schema?.content) {
        schemaScript.innerHTML = schema.content;
      }
    }

    tags.forEach(tag => this.meta.updateTag(tag));
  }

  private getCanonical() {
    const path = getFullPath(this.route.snapshot);
    const origin = this.browserService.getOrigin();
    return `${origin}/${path.join('/')}`;
  }

  private async getMetaTags(url: string, titleOnly?: boolean) {
    const data = getAllRouteData(this.route.snapshot);
    const path = getFullPath(this.route.snapshot);
    let title = data.title
      ? this.browserService.isTenantDomain()
        ? data.title
        : `${data.title} · Picflow`
      : isNotFoundRoute(url, {
            notFound: this.state.nextPageIsNotFound,
          })
        ? '404 · Page not found'
        : 'Picflow';

    const tags: MetaDefinition[] = [];
    let imageUrl = data.imageUrl ?? 'https://picflow.com/assets/sharing/default.jpg';

    let type = 'website';
    let description =
      data.description ?? 'The best way for Pro Photographers to Share, Proof and Deliver Work. Try Picflow for Free!';
    let robots = 'index,follow,max-image-preview:large';
    let schema: Schema;
    let themeColor = data.themeColor || '#FFFFFF';

    if (!titleOnly) {
      tags.push({ property: 'og:url', content: this.getCanonical() });
    }

    // Storyblok pages
    if (data.story && path[0] !== 'a') {
      title = this.storyblokService.getStoryTitle(data.story);

      description = this.storyblokService.getStoryDescription(data.story);
      if (data.story.content.keyVisual?.filename) {
        imageUrl = data.story.content.keyVisual.filename;
        if (imageUrl.endsWith('.svg')) {
          imageUrl = `${imageUrl}/m/1200x0/filters:format(jpg)`;
        }
      }
      if (data.story.content.noIndex) robots = 'noindex,follow';
      if (path[0] === 'blog') {
        type = 'article';
      }

      if (!titleOnly) {
        schema = await this.storyblokSchemaService.getSchema(data.story, data.relationships);
      }

      const imagesToPreload = this.storyblokService.getStoryImagesToPreload();

      imagesToPreload.forEach(image => {
        const link: HTMLLinkElement = this.document.createElement('link');
        link.setAttribute('rel', 'preload');
        link.setAttribute('as', 'image');
        link.setAttribute('href', image);
        this.document.head.appendChild(link);
      });

      if (data.story.content?.background?.includes('dark')) {
        themeColor = '#111319';
      } else if (data.story.content?.background?.includes('black')) {
        themeColor = '#000000';
      }

      if (data.story.content.body?.[0]?.component === 'HomeTeaser') {
        themeColor = '#000000';
      }
    }

    if (data.story === false) {
      robots = 'noindex,follow';
    }

    // Auth pages etc.
    if (data.meta) {
      const meta = data.meta;
      if (meta.title) title = meta.title;
      if (meta.description) description = meta.description;
      if (meta.imageUrl) imageUrl = meta.imageUrl;
    }

    // Some pages should not be indexed
    if (data.noIndex) robots = 'noindex,follow';

    tags.push({ property: 'og:image', content: imageUrl });
    tags.push({ property: 'og:title', content: title });
    tags.push({ name: 'description', content: description });
    tags.push({ property: 'og:description', content: description });
    tags.push({ property: 'og:type', content: type });
    tags.push({ name: 'robots', content: robots });
    tags.push({ name: 'theme-color', content: themeColor });

    const stopZoom = this.browserService.isTenantDomain() || path[0] === 'a';

    tags.push({
      name: 'viewport',
      content: stopZoom
        ? 'width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover, user-scalable=no, shrink-to-fit=no'
        : 'width=device-width, initial-scale=1, viewport-fit=cover',
    });

    // Twitter spesific image
    tags.push({ content: imageUrl, name: 'twitter:image' });

    tags.push({ content: JSON.stringify(schema), name: 'schema' });

    return tags;
  }
}
